import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Clock, Phone, Car, 
  Train, Accessibility, Wine, Calendar, ExternalLink
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { SHOW_INFO, SHOW_DATES, VENUES } from '../data/showData';

// Componente per le info di un singolo venue
const VenueSection = ({ venue, isDefault }) => (
  <section id={venue.id} className="mb-16 scroll-mt-24">
    {/* Header Venue */}
    <div className="bg-gradient-to-r from-[#E91E63]/20 to-[#D946EF]/10 rounded-2xl border border-[#E91E63]/30 p-6 md:p-8 mb-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {venue.name}
          </h2>
          <p className="text-gray-300 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#E91E63]" />
            {venue.address}, {venue.city} {venue.zone && `(${venue.zone})`}
          </p>
        </div>
        <a
          href={venue.mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Apri in Google Maps
        </a>
      </div>
    </div>

    {/* Grid Info */}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {/* Contact */}
      <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
        <Phone className="w-8 h-8 text-[#25D366] mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Contatti</h3>
        <a 
          href={`whatsapp://send?phone=39${venue.contact.phone}`}
          className="text-[#25D366] hover:underline block"
        >
          {venue.contact.phoneDisplay}
        </a>
        <p className="text-gray-400 text-sm mt-1">Per info e prenotazioni</p>
      </div>

      {/* Parking */}
      <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
        <Car className="w-8 h-8 text-[#FFD700] mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Parcheggio</h3>
        <p className="text-gray-300 text-sm mb-2">{venue.parking.description}</p>
        <ul className="text-gray-400 text-xs space-y-1">
          {venue.parking.details.map((detail, i) => (
            <li key={i}>• {detail}</li>
          ))}
        </ul>
      </div>

      {/* Public Transport */}
      <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
        <Train className="w-8 h-8 text-[#D946EF] mb-4" />
        <h3 className="text-lg font-semibold text-white mb-3">Mezzi pubblici</h3>
        <p className="text-gray-300 text-xs mb-3">{venue.publicTransport.description}</p>
        <div className="space-y-2 text-xs">
          {venue.publicTransport.lines.map((line, i) => (
            <p key={i} className="text-gray-300">
              <span className="text-[#D946EF] font-medium">{line.type}:</span> {line.stop}
            </p>
          ))}
        </div>
      </div>

      {/* Opening Time */}
      <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
        <Clock className="w-8 h-8 text-[#FFD700] mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Apertura</h3>
        <p className="text-gray-300">{venue.openingTime}</p>
      </div>

      {/* Accessibility */}
      <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
        <Accessibility className="w-8 h-8 text-[#25D366] mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Accessibilità</h3>
        <p className="text-gray-300 text-sm">{venue.accessibility}</p>
      </div>

      {/* Bar */}
      {venue.hasBar && (
        <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 card-hover">
          <Wine className="w-8 h-8 text-[#D946EF] mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Bar</h3>
          <p className="text-gray-300 text-sm">{venue.barInfo}</p>
        </div>
      )}
    </div>

    {/* Map */}
    <div className="bg-[#121212] rounded-2xl border border-white/5 overflow-hidden mb-8">
      <iframe
        src={venue.mapUrl}
        width="100%"
        height="300"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title={`Mappa ${venue.name}`}
        className="grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
      />
    </div>

    {/* Venue FAQ */}
    {venue.faq && venue.faq.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-white mb-4">
          FAQ - {venue.name}
        </h3>
        <Accordion type="single" collapsible className="space-y-3">
          {venue.faq.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`venue-faq-${venue.id}-${index}`}
              className="bg-[#121212] rounded-xl border border-white/5 px-6"
            >
              <AccordionTrigger className="text-left text-white hover:text-[#FFD700] py-4 text-sm">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 pb-4 text-sm">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    )}
  </section>
);

const Info = () => {
  const location = useLocation();
  
  // Scroll to venue section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Get unique venues from dates
  const activeVenueIds = [...new Set(SHOW_DATES.map(d => d.venueId || 'teatro-sant-anna'))];
  const activeVenues = activeVenueIds.map(id => VENUES[id]).filter(Boolean);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a]">
      <div className="container-custom max-w-5xl">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFD700] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Info & <span className="text-[#FFD700]">FAQ</span>
          </h1>
          <p className="text-gray-400 text-lg mb-6">
            Tutto quello che devi sapere su Sister Tac
          </p>
          
          {/* Quick Navigation to venues */}
          {activeVenues.length > 1 && (
            <div className="flex flex-wrap justify-center gap-3">
              {activeVenues.map(venue => (
                <a
                  key={venue.id}
                  href={`#${venue.id}`}
                  className="px-4 py-2 bg-[#121212] border border-white/10 rounded-full text-white hover:border-[#E91E63] hover:text-[#E91E63] transition-colors text-sm"
                >
                  📍 {venue.name} - {venue.city}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Venue Sections */}
        {activeVenues.map((venue, index) => (
          <VenueSection 
            key={venue.id} 
            venue={venue} 
            isDefault={index === 0}
          />
        ))}

        {/* Show Info (Common) - Solo dettagli sullo spettacolo */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#FFD700]" />
            Informazioni sullo spettacolo
          </h2>
          <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
            <div className="grid md:grid-cols-3 gap-6 text-gray-300">
              <div>
                <p className="text-white/60 text-sm mb-1">Durata</p>
                <p className="text-white font-medium">{SHOW_INFO.duration}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">Intervallo</p>
                <p className="text-white font-medium">{SHOW_INFO.interval}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">Organizzazione</p>
                <p className="text-white font-medium">{SHOW_INFO.organization}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <div className="text-center bg-gradient-to-r from-[#121212] to-[#1a1a1a] rounded-2xl border border-[#FFD700]/20 p-8 md:p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Altre domande?
          </h2>
          <p className="text-gray-400 mb-6">
            Contatta direttamente il teatro della data che ti interessa! 💬
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {activeVenues.map(venue => (
              <a 
                key={venue.id}
                href={`whatsapp://send?phone=39${venue.contact.phone}`}
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3 rounded-full transition-colors"
              >
                📍 {venue.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Info;
